/**
 * Permission Types for Frontend
 * Used for client-side authorization checks
 */

// =============================================================================
// Permission Resources and Actions
// =============================================================================

export type ResourceType =
  | 'contacts'
  | 'campaigns'
  | 'chat'
  | 'users'
  | 'settings'
  | 'analytics'
  | 'roles'
  | 'flows'
  | 'knowledgeBases'
  | 'demands'
  | 'electionScenarios'
  | 'supporters'
  | 'teams'
  | 'organizations';

export type ContactsAction = 'view' | 'create' | 'edit' | 'delete' | 'import' | 'export';
export type CampaignsAction = 'view' | 'create' | 'edit' | 'delete' | 'start' | 'pause';
export type ChatAction = 'view' | 'send' | 'assign' | 'close';
export type UsersAction = 'view' | 'invite' | 'edit' | 'remove';
export type SettingsAction = 'view' | 'edit' | 'edit_theme' | 'edit_public_page';
export type AnalyticsAction = 'view' | 'export';
export type RolesAction = 'view' | 'create' | 'edit' | 'delete';
export type FlowsAction = 'view' | 'create' | 'edit' | 'delete' | 'publish';
export type KnowledgeBasesAction = 'view' | 'create' | 'edit' | 'delete' | 'upload';
export type DemandsAction = 'view' | 'create' | 'edit' | 'delete' | 'assign' | 'notify';
export type ElectionScenariosAction =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'export'
  | 'ai_suggest';
export type SupportersAction = 'view' | 'create' | 'edit' | 'delete' | 'promote';
export type TeamsAction = 'view' | 'create' | 'edit' | 'delete';
export type OrganizationsAction = 'view' | 'create' | 'edit' | 'delete' | 'import';

export type ActionType =
  | ContactsAction
  | CampaignsAction
  | ChatAction
  | UsersAction
  | SettingsAction
  | AnalyticsAction
  | RolesAction
  | FlowsAction
  | KnowledgeBasesAction
  | DemandsAction
  | ElectionScenariosAction
  | SupportersAction
  | TeamsAction
  | OrganizationsAction;

// =============================================================================
// Permission String Format
// =============================================================================

// Permission strings are in format "resource:action"
export type PermissionString = `${ResourceType}:${string}`;

// =============================================================================
// Role Types
// =============================================================================

export type SystemRoleId = 'admin' | 'gerente' | 'operador' | 'visualizador';

export interface RolePermissions {
  contacts: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    import: boolean;
    export: boolean;
    view_vip_address: boolean;
  };
  campaigns: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    start: boolean;
    pause: boolean;
  };
  chat: {
    view: boolean;
    send: boolean;
    assign: boolean;
    close: boolean;
  };
  users: {
    view: boolean;
    invite: boolean;
    edit: boolean;
    remove: boolean;
  };
  settings: {
    view: boolean;
    edit: boolean;
    edit_theme: boolean;
    edit_public_page: boolean;
  };
  analytics: {
    view: boolean;
    export: boolean;
  };
  roles: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
  };
  flows: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    publish: boolean;
  };
  knowledgeBases: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    upload: boolean;
  };
  demands: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    assign: boolean;
    notify: boolean;
  };
  electionScenarios: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    export: boolean;
    ai_suggest: boolean;
  };
  supporters: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    promote: boolean;
  };
  teams: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
  };
  organizations: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    import: boolean;
  };
}

export interface Role {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: RolePermissions;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// JWT Claims (from Cognito Pre Token Generation)
// =============================================================================

export interface JwtClaims {
  sub: string;
  email: string;
  tenant_id: string;
  user_id: string;
  role: string;
  permissions: string; // JSON stringified array
  subscription_status: string;
  plan: string;
}

// =============================================================================
// Permission Helpers
// =============================================================================

/**
 * Parse permissions from JWT claim string
 */
export function parsePermissions(permissionsJson: string): Set<string> {
  try {
    const permissions = JSON.parse(permissionsJson) as string[];
    return new Set(permissions);
  } catch {
    return new Set();
  }
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(
  permissions: Set<string> | string[],
  resource: ResourceType,
  action: string
): boolean {
  const permSet = permissions instanceof Set ? permissions : new Set(permissions);
  const permissionKey = `${resource}:${action}`;

  // Admin has all permissions
  if (permSet.has('*:*')) {
    return true;
  }

  return permSet.has(permissionKey);
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
  permissions: Set<string> | string[],
  checks: Array<{ resource: ResourceType; action: string }>
): boolean {
  return checks.some(({ resource, action }) => hasPermission(permissions, resource, action));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(
  permissions: Set<string> | string[],
  checks: Array<{ resource: ResourceType; action: string }>
): boolean {
  return checks.every(({ resource, action }) => hasPermission(permissions, resource, action));
}
